import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Mail, TrendingUp, Eye, MousePointerClick } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EmailTemplateVariant {
  id: number;
  sequenceType: string;
  step: number;
  variant: string;
  subject: string;
  templateName: string;
  isActive: boolean;
  conversionRate: number | null;
  sentCount: number;
  openCount: number;
  clickCount: number;
  createdAt: Date;
}

export default function AdminEmailTemplates() {
  const { toast } = useToast();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplateVariant | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: templates, isLoading } = useQuery<{ variants: EmailTemplateVariant[] }>({
    queryKey: ['/api/admin/email-templates'],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<EmailTemplateVariant> }) => {
      return await apiRequest(`/api/admin/email-templates/${id}`, 'PATCH', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email-templates'] });
      setEditingTemplate(null);
      toast({
        title: 'Template updated',
        description: 'Email template has been updated successfully.',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/admin/email-templates/${id}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email-templates'] });
      toast({
        title: 'Template deleted',
        description: 'Email template has been deleted successfully.',
      });
    },
  });

  const handleToggleActive = (template: EmailTemplateVariant) => {
    updateMutation.mutate({
      id: template.id,
      updates: { isActive: !template.isActive },
    });
  };

  const calculateConversionRate = (template: EmailTemplateVariant) => {
    if (template.sentCount === 0) return 0;
    const openRate = (template.openCount / template.sentCount) * 100;
    return openRate.toFixed(1);
  };

  const calculateClickRate = (template: EmailTemplateVariant) => {
    if (template.openCount === 0) return 0;
    const clickRate = (template.clickCount / template.openCount) * 100;
    return clickRate.toFixed(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading email templates...</div>
      </div>
    );
  }

  const groupedTemplates = templates?.variants.reduce((acc, template) => {
    const key = `${template.sequenceType}-${template.step}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(template);
    return acc;
  }, {} as Record<string, EmailTemplateVariant[]>) || {};

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Email Template Management</h1>
          <p className="text-muted-foreground">Manage email templates and A/B test variants</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-template">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Email Template Variant</DialogTitle>
              <DialogDescription>
                Add a new email template variant for A/B testing
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Sequence Type</Label>
                <Select>
                  <SelectTrigger data-testid="select-sequence-type">
                    <SelectValue placeholder="Select sequence type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="welcome">Welcome</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Step Number</Label>
                <Input type="number" placeholder="1" data-testid="input-step-number" />
              </div>
              <div>
                <Label>Variant</Label>
                <Select>
                  <SelectTrigger data-testid="select-variant">
                    <SelectValue placeholder="Select variant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Variant A</SelectItem>
                    <SelectItem value="B">Variant B</SelectItem>
                    <SelectItem value="C">Variant C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject Line</Label>
                <Input placeholder="Welcome to SnuggleCrafters!" data-testid="input-subject" />
              </div>
              <div>
                <Label>Template Name</Label>
                <Input placeholder="welcome-step-1-variant-a" data-testid="input-template-name" />
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="active" data-testid="switch-active" />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)} data-testid="button-cancel-create">Cancel</Button>
              <Button data-testid="button-save-template">Save Template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedTemplates).map(([key, variants]) => {
          const [sequenceType, step] = key.split('-');
          return (
            <Card key={key}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {sequenceType.charAt(0).toUpperCase() + sequenceType.slice(1)} - Step {step}
                </CardTitle>
                <CardDescription>
                  {variants.length} variant{variants.length !== 1 ? 's' : ''} configured
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Variant</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Open Rate</TableHead>
                      <TableHead>Click Rate</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variants.map((template) => (
                      <TableRow key={template.id} data-testid={`row-template-${template.id}`}>
                        <TableCell>
                          <Badge variant="outline">{template.variant}</Badge>
                        </TableCell>
                        <TableCell>{template.subject}</TableCell>
                        <TableCell className="font-mono text-sm">{template.templateName}</TableCell>
                        <TableCell>
                          {template.isActive ? (
                            <Badge className="bg-green-500">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>{template.sentCount}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {calculateConversionRate(template)}%
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MousePointerClick className="w-3 h-3" />
                            {calculateClickRate(template)}%
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(template)}
                              data-testid={`button-toggle-${template.id}`}
                            >
                              <Switch checked={template.isActive} className="pointer-events-none" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTemplate(template)}
                              data-testid={`button-edit-${template.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm('Are you sure you want to delete this template?')) {
                                  deleteMutation.mutate(template.id);
                                }
                              }}
                              data-testid={`button-delete-${template.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
            <DialogDescription>Update email template settings</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Subject Line</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  data-testid="input-edit-subject"
                />
              </div>
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editingTemplate.templateName}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, templateName: e.target.value })}
                  data-testid="input-edit-template-name"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={editingTemplate.isActive}
                  onCheckedChange={(checked) => setEditingTemplate({ ...editingTemplate, isActive: checked })}
                  data-testid="switch-edit-active"
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)} data-testid="button-cancel-edit">Cancel</Button>
            <Button
              onClick={() => {
                if (editingTemplate) {
                  updateMutation.mutate({
                    id: editingTemplate.id,
                    updates: {
                      subject: editingTemplate.subject,
                      templateName: editingTemplate.templateName,
                      isActive: editingTemplate.isActive,
                    },
                  });
                }
              }}
              data-testid="button-update-template"
            >
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
